/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { auth, googleProvider } from "../../lib/firebase";
import { useLanguage } from "../../contexts/LanguageContext";
import { getFirebaseErrorMessage } from "../../utils/firebaseErrorHandler";
import { isInAppBrowser } from "../../utils/browser";
import { track } from "../../utils/analytics";
import { saveReferral, updateUserName } from "./api";
import { ErrorBox, Field, PrimaryButton, Section, Spinner, StickyFooter, TextInput } from "./ui";

// The first /start screen, shown to signed-out visitors. It never navigates:
// once Firebase reports the new user, StartFlow swaps this screen for
// Customize on the same URL.

const MIN_PASSWORD = 6; // Firebase's own minimum; checked here to save a round trip.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NO_ERRORS = { name: "", email: "", password: "", form: "", emailInUse: false };

const TERMS_URL = "https://www.loyaltify.io/terms-of-use/";
const PRIVACY_URL = "https://www.loyaltify.io/privacy-policy/";

const STEPS = ["design", "materials", "try"];

function trackSafely(event, props) {
  try {
    track(event, props);
  } catch {
    // ignore
  }
}

// The conversion GTM forwards to GA4 and Meta (CompleteRegistration). Fired as
// soon as the account exists: the profile calls after it are network round
// trips, and an Instagram visitor may close the in-app browser at any moment.
function trackSignUp(method) {
  trackSafely("start_sign_up", { method });
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "sign_up", method, onboarding_flow: "start" });
  } catch {
    // ignore
  }
}

// Runs a follow-up call whose failure must not reach the screen (a sync throw
// included), logging it instead.
function quietly(what, run) {
  return Promise.resolve()
    .then(run)
    .catch((error) => console.error(`Failed to ${what}:`, error));
}

function saveReferralIfAny() {
  const referral = window.promotekit_referral;
  if (!referral) return Promise.resolve();
  return quietly("save the PromoteKit referral", () => saveReferral(referral));
}

// Everything after the account exists. StartFlow is already moving on to
// Customize, so nothing here may hold the screen or surface an error.
function completeProfile(user, name) {
  const followUps = [saveReferralIfAny()];
  if (name) {
    followUps.push(quietly("set the Firebase display name", () => updateProfile(user, { displayName: name })));
    // The backend creates its user from the first ID token it sees, and a
    // fresh email account's token has no name in it yet. Without this the
    // users table (invitations, team list) keeps an empty name.
    followUps.push(quietly("save the user's name", () => updateUserName(name)));
  }
  return Promise.all(followUps);
}

function GoogleIcon() {
  return (
    <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function NextSteps() {
  const { t } = useLanguage();
  return (
    <div className="mt-5">
      <h2 className="text-sm font-semibold text-gray-500">{t("start.signup.nextTitle")}</h2>
      <ol className="mt-2 space-y-2.5">
        {STEPS.map((step, i) => (
          <li key={step} className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EEEBFE] text-sm font-semibold text-[#2F12B8]"
            >
              {i + 1}
            </span>
            <span className="min-w-0 text-sm leading-snug">
              <span className="block font-semibold text-gray-900">{t(`start.signup.steps.${step}.title`)}</span>
              <span className="block text-gray-500">{t(`start.signup.steps.${step}.detail`)}</span>
            </span>
          </li>
        ))}
      </ol>
      <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-green-700">
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
        </svg>
        {t("start.signup.trial")}
      </p>
    </div>
  );
}

export default function StartSignup() {
  const { t } = useLanguage();
  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState(() => new URLSearchParams(location.search).get("email") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState(NO_ERRORS);
  const [pending, setPending] = useState(null); // null | "password" | "google"
  // `pending` is a render-time value, so two taps dispatched before React
  // re-renders both read null. A ref updates synchronously; Firebase corrupts
  // its own promise bookkeeping if a second popup starts while one is open.
  const authPendingRef = useRef(false);
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // Lets the person come back to /start after signing in on /login.
  const loginState = { from: `${location.pathname}${location.search}` };
  const showGoogle = !isInAppBrowser();

  const validate = () => {
    const next = { ...NO_ERRORS };
    if (!name.trim()) next.name = t("start.signup.nameRequired");
    const trimmedEmail = email.trim();
    if (!trimmedEmail) next.email = t("auth.firebaseErrors.missingEmail");
    else if (!EMAIL_PATTERN.test(trimmedEmail)) next.email = t("auth.firebaseErrors.invalidEmail");
    if (!password) next.password = t("auth.firebaseErrors.missingPassword");
    else if (password.length < MIN_PASSWORD) next.password = t("start.signup.passwordTooShort");
    return next;
  };

  const showAuthError = (error, method) => {
    trackSafely("start_sign_up_failed", { method, code: error?.code || "unknown" });
    const message = getFirebaseErrorMessage(error, t);
    switch (error?.code) {
      case "auth/email-already-in-use":
        setErrors({ ...NO_ERRORS, email: message, emailInUse: true });
        emailRef.current?.focus();
        break;
      case "auth/invalid-email":
      case "auth/missing-email":
        setErrors({ ...NO_ERRORS, email: message });
        emailRef.current?.focus();
        break;
      case "auth/weak-password":
      case "auth/missing-password":
        setErrors({ ...NO_ERRORS, password: message });
        passwordRef.current?.focus();
        break;
      default:
        setErrors({ ...NO_ERRORS, form: message });
    }
  };

  const handleSubmit = async (e) => {
    // preventDefault before the guard, or a blocked submit falls through to a
    // native form post.
    e.preventDefault();
    if (authPendingRef.current) return;

    const fieldErrors = validate();
    if (fieldErrors.name || fieldErrors.email || fieldErrors.password) {
      setErrors(fieldErrors);
      const firstInvalid = fieldErrors.name ? nameRef : fieldErrors.email ? emailRef : passwordRef;
      firstInvalid.current?.focus();
      return;
    }

    authPendingRef.current = true;
    setErrors(NO_ERRORS);
    setPending("password");
    const trimmedName = name.trim();
    let result;
    try {
      result = await createUserWithEmailAndPassword(auth, email.trim(), password);
    } catch (error) {
      authPendingRef.current = false;
      setPending(null);
      showAuthError(error, "password");
      return;
    }
    // From here on the account exists: the guard stays set and the button stays
    // busy until StartFlow replaces this screen.
    trackSignUp("password");
    await completeProfile(result.user, trimmedName);
  };

  const handleGoogle = async () => {
    if (authPendingRef.current) return;
    authPendingRef.current = true;
    setErrors(NO_ERRORS);
    setPending("google");
    let result;
    try {
      result = await signInWithPopup(auth, googleProvider);
    } catch (error) {
      authPendingRef.current = false;
      setPending(null);
      if (error?.code === "auth/popup-closed-by-user") return;
      showAuthError(error, "google");
      return;
    }
    // The same button signs an existing Google account in. Counting that as a
    // registration would feed Meta false conversions, so only new accounts are
    // tracked as sign-ups (and only they take a referral, as a sign-up would).
    let isNewUser = true;
    try {
      isNewUser = getAdditionalUserInfo(result)?.isNewUser !== false;
    } catch {
      // Unknown: count it rather than lose a real conversion.
    }
    if (isNewUser) {
      trackSignUp("google");
      await saveReferralIfAny();
    } else {
      trackSafely("start_sign_in", { method: "google" });
    }
  };

  const clearError = (field) => {
    if (!errors[field]) return;
    setErrors((prev) => ({ ...prev, [field]: "", emailInUse: field === "email" ? false : prev.emailInUse }));
  };

  const emailError = errors.email && (
    <>
      {errors.email}
      {errors.emailInUse && (
        <>
          {" "}
          <Link to="/login" state={loginState} className="font-semibold text-[#4522ED] underline underline-offset-2">
            {t("start.signup.signInInstead")}
          </Link>
        </>
      )}
    </>
  );

  const linkClass = "font-medium text-[#4522ED] underline underline-offset-2 hover:text-[#2F12B8]";
  const busy = pending !== null;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="pt-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{t("start.signup.title")}</h1>
        <p className="mt-2 text-base text-gray-600">{t("start.signup.subtitle")}</p>
      </div>

      <NextSteps />

      <Section title={t("auth.signup.title")} className="mt-6">
        {errors.form && <ErrorBox>{errors.form}</ErrorBox>}

        {showGoogle && (
          <>
            <button
              type="button"
              onClick={handleGoogle}
              disabled={busy}
              aria-busy={pending === "google" || undefined}
              className="inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white px-5 text-base font-semibold text-gray-900 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4522ED]"
            >
              {pending === "google" ? <Spinner className="h-5 w-5 text-gray-500" /> : <GoogleIcon />}
              {t("auth.signup.signUpWithGoogle")}
            </button>
            <div className="flex items-center gap-3">
              <div aria-hidden="true" className="h-px flex-1 bg-gray-200" />
              <span className="text-sm text-gray-500">{t("start.signup.orEmail")}</span>
              <div aria-hidden="true" className="h-px flex-1 bg-gray-200" />
            </div>
          </>
        )}

        <Field label={t("auth.signup.fullNameLabel")} htmlFor="start-signup-name" error={errors.name}>
          <TextInput
            ref={nameRef}
            id="start-signup-name"
            name="name"
            type="text"
            autoComplete="name"
            autoCapitalize="words"
            required
            aria-required="true"
            invalid={!!errors.name}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearError("name");
            }}
          />
        </Field>

        <Field label={t("auth.signup.emailLabel")} htmlFor="start-signup-email" error={emailError}>
          <TextInput
            ref={emailRef}
            id="start-signup-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            aria-required="true"
            invalid={!!errors.email}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearError("email");
            }}
          />
        </Field>

        <Field
          label={t("auth.signup.passwordLabel")}
          htmlFor="start-signup-password"
          hint={t("start.signup.passwordHint")}
          error={errors.password}
        >
          <div className="relative">
            <TextInput
              ref={passwordRef}
              id="start-signup-password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              aria-required="true"
              invalid={!!errors.password}
              className="pr-20"
              value={password}
              onChange={(e) => {
                const next = e.target.value;
                setPassword(next);
                if (errors.password && next.length >= MIN_PASSWORD) clearError("password");
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-controls="start-signup-password"
              aria-label={t(showPassword ? "start.signup.hidePasswordLabel" : "start.signup.showPasswordLabel")}
              className="absolute inset-y-0 right-0 flex min-w-11 items-center justify-center rounded-r-xl px-3.5 text-sm font-semibold text-[#4522ED] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#4522ED]"
            >
              {t(showPassword ? "start.signup.hidePassword" : "start.signup.showPassword")}
            </button>
          </div>
        </Field>

        <p className="text-xs leading-relaxed text-gray-500">
          {t("auth.signup.agreedTosAgreement", {
            termsOfUseLink: (
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>
                {t("auth.signup.termsOfUse")}
              </a>
            ),
            privacyPolicyLink: (
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>
                {t("auth.signup.privacyPolicy")}
              </a>
            ),
          })}
        </p>
      </Section>

      <p className="mt-5 text-center text-sm text-gray-600">
        {t("auth.signup.haveAccount")}{" "}
        <Link
          to="/login"
          state={loginState}
          className="inline-flex min-h-11 items-center px-1 font-semibold text-[#4522ED] underline-offset-4 hover:underline"
        >
          {t("auth.signup.signInLink")}
        </Link>
      </p>

      <StickyFooter>
        <PrimaryButton type="submit" loading={pending === "password"} disabled={busy}>
          {t("start.signup.submit")}
        </PrimaryButton>
      </StickyFooter>
    </form>
  );
}
